if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk').dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.sectionId}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

      showToastNotification(message) {
        // Check for existing cart-notification component
        const cartNotification = document.querySelector('cart-notification');

        if (cartNotification && typeof cartNotification.open === 'function') {
          // Use existing notification system
          cartNotification.open();
        } else {
          // Fallback: Create simple toast notification
          const toast = document.createElement('div');
          toast.className = 'cart-toast-notification';
          toast.setAttribute('role', 'status');
          toast.setAttribute('aria-live', 'polite');
          toast.textContent = message;

          // Add inline styles for toast
          Object.assign(toast.style, {
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: '#323232',
            color: '#fff',
            padding: '16px 24px',
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: '9999',
            fontSize: '14px',
            fontFamily: 'var(--font-body-family)',
            animation: 'slideIn 0.3s ease-out',
            width: 'max-content',
            maxWidth: '90vw'
          });

          // Add animation keyframes if not already present
          if (!document.getElementById('toast-animations')) {
            const style = document.createElement('style');
            style.id = 'toast-animations';
            style.textContent = `
              @keyframes slideIn {
                from {
                  transform: translateY(100%);
                  opacity: 0;
                }
                to {
                  transform: translateY(0);
                  opacity: 1;
                }
              }
              @keyframes slideOut {
                from {
                  transform: translateY(0);
                  opacity: 1;
                }
                to {
                  transform: translateY(100%);
                  opacity: 0;
                }
              }
            `;
            document.head.appendChild(style);
          }

          document.body.appendChild(toast);

          // Auto-dismiss after 3 seconds
          setTimeout(() => {
            toast.style.animation = 'slideOut 0.3s ease-in';
            setTimeout(() => toast.remove(), 300);
          }, 3000);
        }
      }

      async updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');

        const ids = Object.keys(items);
        const hasBrochure = this.dataset.includesBrochure === 'true';
        const brochureId = this.dataset.brochureId;

        // Get current cart state to compare quantities
        const currentCartResponse = await fetch('/cart.js');
        const currentCart = await currentCartResponse.json();

        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        try {
          const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          let parsedState = await response.json();

          // Determine if items were added or removed by comparing quantities
          let itemsAdded = false;
          for (const variantId of ids) {
            const newQuantity = items[variantId];
            const currentItem = currentCart.items.find(item => item.variant_id === parseInt(variantId));
            const currentQuantity = currentItem ? currentItem.quantity : 0;

            if (newQuantity > currentQuantity) {
              itemsAdded = true;
              break;
            }
          }

          // Update brochure property if needed
          if ((hasBrochure || brochureId) && itemsAdded) {
            let allPropertiesUpdated = true;

            for (const variantId of ids) {
              const quantity = items[variantId];
              if (quantity > 0) {
                const cartItem = parsedState.items.find(item => item.variant_id === parseInt(variantId));

                if (cartItem) {
                  try {
                    const existingProperties = cartItem.properties || {};
                    const updatedProperties = {
                      ...existingProperties,
                      'Pet Owner Brochure': cartItem.quantity.toString()
                    };
                    if (brochureId) {
                      updatedProperties['_brochure_id'] = brochureId;
                    }

                    const updateResponse = await fetch('/cart/change.js', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        id: cartItem.key,
                        quantity: cartItem.quantity,
                        properties: updatedProperties
                      }),
                    });

                    if (!updateResponse.ok) {
                      allPropertiesUpdated = false;
                      console.error(`[Brochure Bulk] Failed to update property for variant ${variantId}`);
                      continue;
                    }

                    const updatedCart = await updateResponse.json();
                    parsedState.items = updatedCart.items;
                    parsedState.item_count = updatedCart.item_count;
                    parsedState.total_price = updatedCart.total_price;
                  } catch (err) {
                    allPropertiesUpdated = false;
                    console.error('[Brochure Bulk] Error updating property:', err);
                  }
                }
              }
            }

            // Only fetch fresh sections if all properties were updated successfully
            if (allPropertiesUpdated) {
              try {
                const sectionsToRender = this.getSectionsToRender();
                const sectionIds = sectionsToRender.map(section => section.section).join(',');
                const sectionsResponse = await fetch(`${this.getSectionsUrl()}?sections=${sectionIds}`);

                if (sectionsResponse.ok) {
                  const freshSections = await sectionsResponse.json();
                  parsedState.sections = freshSections;
                }
              } catch (err) {
                console.error('[Brochure Bulk] Failed to fetch fresh sections:', err);
              }
            }
          }

          this.renderSections(parsedState, ids);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });

          // Show toast notification ONLY when items are added (not removed)
          if (itemsAdded && parsedState.item_count > 0) {
            const message = window.cartStrings?.item_added_toast;
            this.showToastNotification(message);
          }
        } catch (err) {
          console.error('[Bulk Add] Cart update failed:', err);
        } finally {
          this.selectProgressBar().classList.add('hidden');
          this.setRequestStarted(false);
        }
      }

      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          }
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (section.section === 'cart-drawer') {
            sectionElement?.closest('cart-drawer')?.classList.toggle('is-empty', parsedState.items.length === 0);
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace && parsedState.sections?.[section.section]) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`)?.select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
